/**
 * @fileoverview Finding the live SDK instance from a framework binding.
 * @module lib/features/core/resolve
 *
 * There are two ways a page gets a Flowgrid, and a binding has to work with
 * both:
 *
 *   1. **npm** — the app imports `FlowGrid` and calls `FlowGrid.init()`. The
 *      instance lives on the class; `window.FlowGrid` is never set.
 *   2. **script tag** — `browser.ts` assigns `window.FlowGrid` and the app never
 *      imports anything. A binding bundled by the app is then a *different*
 *      module instance from the one on the page, so the class static is empty.
 *
 * Checking only the window (which the bindings originally did) silently breaks
 * every npm consumer: `useFlag` returns its fallback forever and no visitor
 * ever sees a flag turn on. Checking only the class breaks the script-tag install. So:
 * class first, window second.
 */

import { FlowGrid } from '../../flowgrid';

/** The parts of an instance the bindings reach for. */
export interface ResolvedInstance {
  flags?: unknown;
  setUser?: (
    userId: string | number | null | undefined,
    traits?: { email?: string; name?: string } & Record<string, unknown>,
    opts?: { authEvents?: boolean },
  ) => unknown;
}

interface WindowFlowGrid {
  instance?: () => ResolvedInstance | null;
}

/**
 * The active Flowgrid, or null on the server / before `init()`.
 * Never throws — a binding must degrade to "no engine", not to a broken render.
 */
export function activeInstance(): ResolvedInstance | null {
  try {
    const direct = FlowGrid.instance();
    if (direct) return direct as unknown as ResolvedInstance;
  } catch {
    /* class not initialised in this module instance — try the page global */
  }

  if (typeof window === 'undefined') return null;
  try {
    return (window as unknown as { FlowGrid?: WindowFlowGrid }).FlowGrid?.instance?.() ?? null;
  } catch {
    return null;
  }
}
